import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatMoney } from "@/lib/api";
import { Building2, Users, ReceiptText, TrendingUp, Activity, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

function Stat({ label, value, sub, icon: Icon, testid, accent }) {
  return (
    <div className="bg-white border border-zinc-200 p-6 rounded-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-3">{label}</div>
          <div className="font-heading text-3xl font-black tracking-tight">{value}</div>
          {sub && <div className="mt-2 text-xs text-zinc-500">{sub}</div>}
        </div>
        <div className={`h-10 w-10 grid place-items-center rounded-sm ${accent || "bg-zinc-100"}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/super-admin/stats"); setStats(data); } catch {}
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="sa-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Platform Operations</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight">Super Admin.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">Overview of all client companies, subscriptions, and platform-wide metrics.</p>
        </div>
        <Link to="/super-admin/companies">
          <Button className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5">
            <Sparkles className="h-4 w-4 mr-2" /> Manage Companies
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Client Companies" value={stats?.total_companies ?? "—"} sub={`${stats?.active_companies ?? 0} active · ${stats?.trial_companies ?? 0} trial`} icon={Building2} testid="sa-stat-companies" />
        <Stat label="Total Employees" value={stats?.total_employees ?? "—"} sub="across all clients" icon={Users} accent="bg-zinc-100" testid="sa-stat-employees" />
        <Stat label="Total Invoiced" value={stats ? formatMoney(stats.total_invoiced, "INR") : "—"} sub={`${stats?.total_invoices ?? 0} invoices`} icon={ReceiptText} accent="bg-amber-100 text-amber-700" testid="sa-stat-invoiced" />
        <Stat label="MRR" value={stats ? formatMoney(stats.mrr, "INR") : "—"} sub="Monthly recurring" icon={TrendingUp} accent="bg-orange-100 text-orange-700" testid="sa-stat-mrr" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-6">
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Subscription mix</div>
          <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {stats?.plans && Object.entries(stats.plans).filter(([k]) => k !== "trial").map(([key, p]) => (
              <div key={key} className="border border-zinc-200 p-4 rounded-sm">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{key}</div>
                <div className="font-heading text-2xl font-black">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{p.employee_limit === -1 ? "Unlimited" : `${p.employee_limit} employees`}</div>
                <div className="font-mono text-sm mt-2">{formatMoney(p.price_inr, "INR")}/mo</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Activity</div>
          <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Last 30 days</h3>
          <div className="flex items-center gap-3">
            <Activity className="h-8 w-8 text-[#F97316]" />
            <div>
              <div className="font-heading text-3xl font-black">{stats?.recent_signups_30d ?? "—"}</div>
              <div className="text-xs text-zinc-500">new client signups</div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Suspended</div>
            <div className="font-heading text-xl font-black text-red-600">{stats?.suspended_companies ?? 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
